import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

// A stable identity for "no rows yet". Returning a fresh `[]` from the hook
// would give every consumer a new reference on each render — any section that
// syncs local state from `data` in an effect would then loop forever.
const EMPTY = [];

/** Fetches one admin list when the section first mounts.
 *
 * Each section owns its own data: the console previously fired nine requests
 * on load no matter which tab you opened. Radix only mounts the active
 * TabsContent, so mounting is the trigger.
 */
export function useAdminData(path, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);

  const reload = useCallback(async () => {
    if (!enabled) { setData(EMPTY); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(path);
      setData(r.data ?? EMPTY);
    } catch (e) {
      setData(EMPTY);
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load this section");
    } finally {
      setLoading(false);
    }
  }, [path, enabled]);

  useEffect(() => { reload(); }, [reload]);

  return { data: data ?? EMPTY, loading, loaded: data !== null, reload };
}

export function SectionLoading({ label = "Loading…" }) {
  return (
    <div className="border border-border bg-card px-5 py-12 text-center text-sm text-muted-foreground"
         data-testid="section-loading">{label}</div>
  );
}

export function SectionEmpty({ children, testid }) {
  return (
    <div className="px-5 py-8 text-sm text-muted-foreground text-center" data-testid={testid}>
      {children}
    </div>
  );
}

export const cycleSort = (setter) => (key) => setter((s) =>
  s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
);

export function Header({ cols, sort, onSort }) {
  // Backward-compatible: if `sort`/`onSort` aren't passed, renders plain labels.
  // Otherwise each col becomes `{label, key}` and clicking cycles asc → desc.
  const items = cols.map((c) => (typeof c === "string" ? { label: c } : c));
  // Column headers only make sense once the row is actually a row — below lg
  // each record stacks into its own block, so the header is hidden.
  const colClass = { 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5",
                     6: "lg:grid-cols-6", 7: "lg:grid-cols-7",
                     8: "lg:grid-cols-8", 9: "lg:grid-cols-9" }[items.length] || "lg:grid-cols-4";
  return (
    <div className={`hidden lg:grid ${colClass} px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b border-border`}>
      {items.map((c) => {
        const sortable = onSort && c.key;
        const active = sortable && sort?.key === c.key;
        const arrow = active ? (sort.dir === "asc" ? " ↑" : " ↓") : "";
        return (
          <div
            key={c.label}
            className={sortable ? "cursor-pointer select-none hover:text-foreground transition-colors" : ""}
            onClick={sortable ? () => onSort(c.key) : undefined}
            data-testid={sortable ? `sort-${c.key}` : undefined}
          >
            {c.label}{arrow}
          </div>
        );
      })}
    </div>
  );
}

// Generic client-side sorter — case-insensitive strings, numeric fallback for
// numbers, date-safe for ISO strings.
export function sortRows(rows, sort) {
  if (!sort?.key) return rows;
  const { key, dir } = sort;
  const mul = dir === "asc" ? 1 : -1;
  const val = (r) => {
    const v = key.split(".").reduce((o, k) => (o == null ? o : o[k]), r);
    if (v == null) return "";
    if (typeof v === "number") return v;
    const n = Number(v);
    if (!isNaN(n) && v !== "" && typeof v === "string" && /^-?\d/.test(v)) return n;
    return String(v).toLowerCase();
  };
  return [...rows].sort((a, b) => {
    const av = val(a), bv = val(b);
    if (av < bv) return -1 * mul;
    if (av > bv) return 1 * mul;
    return 0;
  });
}

// Accounting exports get opened in Excel, so values starting with = + - @ are
// prefixed with a quote to stop spreadsheet formula injection.
export function csvCell(value) {
  const s = value === null || value === undefined ? "" : String(value);
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function exportCsv(basename, columns, rows) {
  const header = columns.map(([label]) => csvCell(label)).join(",");
  const body = rows.map(r => columns.map(([, get]) => csvCell(get(r))).join(","));
  // The BOM keeps accented Canadian names readable when Excel opens the file.
  const blob = new Blob(["\uFEFF" + [header, ...body].join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `driveterrain-${basename}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Mirrors STRIPE_ACCOUNT_RE in backend/security.py — used only to flag a value
// that predates server-side validation, never to decide anything.
export const STRIPE_ACCOUNT_RE = /^acct_[A-Za-z0-9_]{6,60}$/;
